///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { computed, Injectable, NgZone, Signal, signal, WritableSignal } from '@angular/core';
import { fromEvent, merge, skipWhile, startWith, Subscription } from 'rxjs';
import { AutoLogoutWorkerService, MessageType } from '../auth/auto-logout-worker.service';
import { outsideZone } from '@siklu/sui-lib/infrastructure';

@Injectable({ providedIn: 'root' })
export class UserActivityService {
    private readonly counter: WritableSignal<number | null> = signal(null);
    private readonly isCountdownActive: Signal<boolean> = computed(() => this.counter() !== null);

    private subscription: Subscription;

    constructor(
        private readonly ngZone: NgZone,
        private readonly autoLogoutWorkerService: AutoLogoutWorkerService
    ) {}

    setCountdownCounter(counter: number | null): void {
        return this.counter.set(counter);
    }

    getCountdownCounter(): Signal<number> {
        return this.counter.asReadonly();
    }

    startTrackUserActivity(): void {
        const mousedown$ = fromEvent(document, 'mousedown').pipe(outsideZone(this.ngZone));
        const mousemove$ = fromEvent(document, 'mousemove').pipe(
            skipWhile(() => !this.isCountdownActive()),
            outsideZone(this.ngZone)
        );
        this.subscription = merge(mousedown$, mousemove$)
            .pipe(startWith(true))
            .subscribe(() => {
                this.ngZone.run(() => {
                    this.setCountdownCounter(null);
                    this.autoLogoutWorkerService.postMessage({ type: MessageType.USER_ACTIVITY });
                });
            });
    }

    stopTrackUserActivity(): void {
        this.setCountdownCounter(null);
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }
}
